import { useQueries } from '@tanstack/react-query';
import { Box, Flex, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { Activity, Database, Loader2, Radio, Server, Wifi } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { getHealth, getStats, getSummary } from '../api/client';
import type { Stats, Summary } from '../api/types';
import { count, relativeTime } from '../lib/format';

export function StatusView() {
  const [healthQuery, statsQuery, summaryQuery] = useQueries({
    queries: [
      {
        queryKey: ['health'],
        queryFn: ({ signal }) => getHealth({ signal }),
        refetchInterval: 5_000,
      },
      {
        queryKey: ['stats'],
        queryFn: ({ signal }) => getStats({ signal }),
        refetchInterval: 5_000,
      },
      {
        queryKey: ['summary'],
        queryFn: ({ signal }) => getSummary({ signal }),
        refetchInterval: 5_000,
      },
    ],
  });
  const stats = statsQuery.data ?? healthQuery.data?.stats;
  const summary = summaryQuery.data;
  const isFetching = healthQuery.isFetching || statsQuery.isFetching || summaryQuery.isFetching;
  const isError = healthQuery.isError || statsQuery.isError || summaryQuery.isError;

  return (
    <Stack as="section" mt="8" gap="5">
      <Flex
        flexDir={{ base: 'column', xl: 'row' }}
        gap="3"
        align={{ xl: 'flex-end' }}
        justify={{ xl: 'space-between' }}
      >
        <Stack gap="3">
          <Flex align="center" gap="3">
            <Box color="signal.blue">
              <Radio size={28} />
            </Box>
            <Heading as="h1" fontSize="3xl" fontWeight="semibold" letterSpacing="tight">
              Status
            </Heading>
          </Flex>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="text.muted">
            Operational health from the live backend endpoints.
          </Text>
        </Stack>
        {isFetching ? (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            borderRadius="oqPanel"
            px="4"
            py="2.5"
            layerStyle="glassPanel"
          >
            <Box as="span" display="inline-flex" alignItems="center">
              <Loader2 className="oq-spin" size={16} />
            </Box>
            <Text as="span" fontSize="sm" color="text.muted">
              Refreshing
            </Text>
          </Box>
        ) : null}
      </Flex>

      {isError ? (
        <Box
          mt="5"
          borderWidth="1px"
          borderColor="signal.warn/20"
          bg="signal.warn/10"
          px="4"
          py="3"
          borderRadius="oqPanel"
        >
          <Text fontSize="sm" color="signal.warn">
            One or more status endpoints did not respond.
          </Text>
        </Box>
      ) : null}

      <SimpleGrid gap="3" columns={{ base: 1, md: 2, xl: 4 }}>
        <StatusCard
          icon={<Server />}
          title="API Health"
          value={healthQuery.data?.ok ? 'OK' : healthQuery.isLoading ? 'Checking' : 'Unavailable'}
          tone={healthQuery.data?.ok ? 'good' : 'warn'}
        />
        <StatusCard
          icon={<Wifi />}
          title="Live Stream"
          value={streamLabel(summary)}
          detail={summary?.last_message_at ? `Last message ${relativeTime(summary.last_message_at)}` : 'Waiting for messages'}
          tone={(summary?.live_streams_connected ?? 0) > 0 ? 'good' : 'warn'}
        />
        <StatusCard
          icon={<Database />}
          title="Markets"
          value={stats ? count(stats.markets) : 'waiting'}
          detail={stats ? `${count(stats.active_assets)} active assets` : undefined}
        />
        <StatusCard
          icon={<Activity />}
          title="News Enrichment"
          value="Not exposed"
          detail="No news status endpoint is available."
        />
      </SimpleGrid>

      <SimpleGrid gap="4" columns={{ base: 1, xl: 2 }}>
        <StatusPanel title="Message Counters">
          <CounterGrid stats={stats} />
        </StatusPanel>
        <StatusPanel title="Discovery">
          <Stack gap="3" fontSize="sm">
            <StatusLine label="Market count" value={stats ? count(stats.markets) : 'waiting'} />
            <StatusLine label="Asset count" value={stats ? count(stats.assets) : 'waiting'} />
            <StatusLine label="Resolved assets" value={stats ? count(stats.resolved_assets) : 'waiting'} />
            <StatusLine label="Last discovery" value={stats?.last_discovery_at ? relativeTime(stats.last_discovery_at) : 'waiting'} />
            <StatusLine label="Last message" value={stats?.last_message_at ? relativeTime(stats.last_message_at) : 'waiting'} />
          </Stack>
        </StatusPanel>
      </SimpleGrid>
    </Stack>
  );
}

function StatusCard({
  icon,
  title,
  value,
  detail,
  tone = 'neutral',
}: {
  icon: ReactElement;
  title: string;
  value: string;
  detail?: string | undefined;
  tone?: 'neutral' | 'good' | 'warn' | undefined;
}) {
  const valueColor = tone === 'good' ? 'signal.good' : tone === 'warn' ? 'signal.warn' : 'text.primary';
  return (
    <Box borderRadius="oqPanel" p="4" layerStyle="glassPanel">
      <Flex align="flex-start" justify="space-between" gap="4">
        <Stack gap="1">
          <Text fontSize="sm" color="text.muted">
            {title}
          </Text>
          <Text fontSize="2xl" fontWeight="semibold" color={valueColor}>
            {value}
          </Text>
          {detail ? (
            <Text mt="1" fontSize="xs" color="text.muted">
              {detail}
            </Text>
          ) : null}
        </Stack>
        <Box
          borderRadius="oqPanel"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.200"
          p="2"
          color="signal.blue"
        >
          <Box w="5" h="5" display="inline-flex" alignItems="center" justifyContent="center">
            {icon}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

function StatusPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box as="section" overflow="hidden" borderRadius="oqPanel" layerStyle="glassPanel">
      <Box borderBottomWidth="1px" borderColor="border.subtle" px="5" py="3">
        <Heading as="h2" fontSize="lg" fontWeight="semibold">
          {title}
        </Heading>
      </Box>
      <Box p="5">{children}</Box>
    </Box>
  );
}

function CounterGrid({ stats }: { stats?: Stats | undefined }) {
  const rows = [
    ['Total messages', stats ? count(stats.messages) : 'waiting'],
    ['Book messages', stats ? count(stats.book_messages) : 'waiting'],
    ['Price messages', stats ? count(stats.price_messages) : 'waiting'],
    ['Trade messages', stats ? count(stats.trade_messages) : 'waiting'],
    ['Best bid/ask', stats ? count(stats.best_bid_ask_messages) : 'waiting'],
    ['Unknown messages', stats ? count(stats.unknown_messages) : 'waiting'],
  ] as const;

  return (
    <SimpleGrid gap="3" columns={{ base: 1, md: 2 }}>
      {rows.map(([label, value]) => (
        <Box key={label} borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="blackAlpha.100" p="4">
          <Text fontSize="xs" textTransform="uppercase" color="text.muted">
            {label}
          </Text>
          <Text mt="2" fontSize="xl" fontWeight="semibold" color="text.primary">
            {value}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}

function StatusLine({ label, value }: { label: string; value: string }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap="4"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      py="2.5"
      _last={{ borderBottomWidth: 0 }}
    >
      <Text as="span" color="text.muted">
        {label}
      </Text>
      <Text as="span" textAlign="right" fontWeight="semibold" color="text.primary">
        {value}
      </Text>
    </Flex>
  );
}

function streamLabel(summary?: Summary) {
  if (!summary) return 'Checking';
  return summary.live_streams_connected > 0 ? 'Live' : 'Waiting';
}